import type { FC, ReactElement } from 'react';
import { useCallback, useMemo } from 'react';

import { DealerName } from '../../../../components/dealerName/DealerName';
import { useAudioTrigger } from '../../../../controllers/audioController/hooks';
import { chatCommunicationController } from '../../../../controllers/chatCommunication/ChatCommunicationControllerConnector';
import { generateMessage } from '../../../../utils/chat';
import { MessageInput } from '../../messageInput/MessageInput';

import styles from './ChatFooter.module.css';

export interface ChatFooterProps {
  casinoId: string;
  dealerName: string;
  isBetRequiredToChat: boolean;
  isGameChat: boolean;
  isGameChatBlocked: boolean;
  isLiveSupportChatBlocked: boolean;
  isGameChatUnavailable: boolean;
  tableId: string;
  userId: string;
  isSpectator: boolean;
  gameType: string;
  onInputFocus: (inputFocused: boolean) => void;
  cashierCallback?: () => void;
}

export const ChatFooter: FC<ChatFooterProps> = ({
  cashierCallback,
  casinoId,
  dealerName,
  isBetRequiredToChat,
  isGameChat,
  isGameChatBlocked,
  isGameChatUnavailable,
  isLiveSupportChatBlocked,
  tableId,
  userId,
  isSpectator,
  gameType,
  onInputFocus,
}): ReactElement => {
  const audioTrigger = useAudioTrigger();
  const isChatBlocked = isGameChat ? isGameChatBlocked : isLiveSupportChatBlocked;

  const placeholder = useMemo(() => {
    if (isSpectator) return 'CHAT_NOT_AVAILABLE';
    if (isChatBlocked) return 'CHAT_IS_BLOCKED';
    return 'TYPE_MESSAGE';
  }, [isSpectator, isChatBlocked]);

  const handleSendMessage = useCallback(
    (input: string) => {
      if (input.trim() !== '') {
        audioTrigger.play({ name: 'menuClick' });
        // Send message to chat server via websocket
        chatCommunicationController.sendMessage(
          JSON.stringify(generateMessage(userId, input, tableId, casinoId, isGameChat))
        );
      }
    },
    [audioTrigger, casinoId, isGameChat, tableId, userId]
  );

  return (
    <div className={styles.chatFooter}>
      <MessageInput
        cashierCallback={cashierCallback}
        isBetRequiredToChat={isBetRequiredToChat}
        isGameChat={isGameChat}
        isGameChatUnavailable={isGameChatUnavailable}
        disabled={isChatBlocked}
        placeholder={placeholder}
        onSubmit={handleSendMessage}
        onInputFocus={onInputFocus}
      />

      <div className={styles.chatFooter__dealer}>
        <DealerName gameType={gameType} name={dealerName} />
      </div>
    </div>
  );
};
